import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Activity,
  Calendar,
  Car,
  Clock,
  MapPin,
  Phone,
  User,
  AlertCircle,
  CheckCircle,
  RefreshCw
} from 'lucide-react';

const DispatchBoard = () => {
  const navigate = useNavigate();
  const [boardData, setBoardData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');

  const fetchData = React.useCallback(async () => {
    try {
      const [boardResponse, driversResponse] = await Promise.all([
        authFetch(`/api/dispatch/board`),
        authFetch(`/api/drivers?is_active=true`)
      ]);

      const boardData = await boardResponse.json();
      const driversData = await driversResponse.json();

      setBoardData(boardData);
      setDrivers(driversData);
    } catch (err) {
      console.error('[DispatchBoard] Failed to fetch board data:', err);
      // Retry on next interval
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Auth is enforced server-side; authFetch handles the redirect on 401.
    fetchData();
    // Refresh every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [navigate, fetchData]);

  const handleAssignDriver = async () => {
    if (!selectedTrip || !selectedDriver) return;

    try {
      const response = await authFetch(`/api/dispatch/assign?booking_id=${selectedTrip.id}&driver_id=${selectedDriver}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setSelectedTrip(null);
        setSelectedDriver('');
        fetchData();
      } else {
        alert('Failed to assign driver');
      }
    } catch (err) {
      console.error('Failed to assign driver:', err);
      alert('Failed to assign driver');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'Confirmed': { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      'Assigned': { color: 'bg-purple-100 text-purple-800', icon: Car },
      'DriverEnRoute': { color: 'bg-green-100 text-green-800', icon: MapPin },
      'InProgress': { color: 'bg-green-100 text-green-800', icon: Activity },
      'Completed': { color: 'bg-gray-100 text-gray-800', icon: CheckCircle }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: AlertCircle };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        {status}
      </span>
    );
  };

  const TripCard = ({ trip, onClick }) => (
    <Card
      className="mb-3 border-2 hover:border-[#F9A825] transition-all cursor-pointer"
      onClick={() => onClick(trip)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <p className="font-semibold text-[#004C97]">{trip.customer_name}</p>
            <p className="text-sm text-gray-600">{trip.patient_name}</p>
          </div>
          {getStatusBadge(trip.status)}
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-start space-x-2">
            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-gray-600 line-clamp-1">{trip.pickup_address}</p>
              <p className="text-gray-600 line-clamp-1">→ {trip.destination}</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{trip.date}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{trip.time}</span>
            </div>
          </div>

          {trip.assigned_driver_name && (
            <div className="flex items-center space-x-1 text-green-600">
              <Car className="w-4 h-4" />
              <span className="text-sm font-medium">{trip.assigned_driver_name}</span>
            </div>
          )}

          <div className="flex items-center space-x-1">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-xs text-gray-500">{trip.mobility_type}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading || !boardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F9A825] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dispatch board...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#004C97] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/dispatch/dashboard')}
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl font-bold text-white">Dispatch Board</h1>
              <Badge variant="secondary" className="bg-[#F9A825] text-white">
                Live
              </Badge>
            </div>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#004C97]"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Main Board */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* Unassigned Column */}
          <div>
            <div className="bg-yellow-100 border-2 border-yellow-300 rounded-lg p-3 mb-4">
              <h3 className="font-semibold text-yellow-900 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5" />
                <span>Unassigned ({boardData.unassigned.length})</span>
              </h3>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
              {boardData.unassigned.map(trip => (
                <TripCard key={trip.id} trip={trip} onClick={setSelectedTrip} />
              ))}
            </div>
          </div>

          {/* Assigned Column */}
          <div>
            <div className="bg-purple-100 border-2 border-purple-300 rounded-lg p-3 mb-4">
              <h3 className="font-semibold text-purple-900 flex items-center space-x-2">
                <Car className="w-5 h-5" />
                <span>Assigned ({boardData.assigned.length})</span>
              </h3>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
              {boardData.assigned.map(trip => (
                <TripCard key={trip.id} trip={trip} onClick={setSelectedTrip} />
              ))}
            </div>
          </div>

          {/* En Route Column */}
          <div>
            <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-3 mb-4">
              <h3 className="font-semibold text-blue-900 flex items-center space-x-2">
                <MapPin className="w-5 h-5" />
                <span>En Route ({boardData.en_route.length})</span>
              </h3>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
              {boardData.en_route.map(trip => (
                <TripCard key={trip.id} trip={trip} onClick={setSelectedTrip} />
              ))}
            </div>
          </div>

          {/* Active Column */}
          <div>
            <div className="bg-green-100 border-2 border-green-300 rounded-lg p-3 mb-4">
              <h3 className="font-semibold text-green-900 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Active ({boardData.active.length})</span>
              </h3>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
              {boardData.active.map(trip => (
                <TripCard key={trip.id} trip={trip} onClick={setSelectedTrip} />
              ))}
            </div>
          </div>

          {/* Completed Column */}
          <div>
            <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-3 mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Completed ({boardData.completed.length})</span>
              </h3>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
              {boardData.completed.map(trip => (
                <TripCard key={trip.id} trip={trip} onClick={setSelectedTrip} />
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Assignment Modal */}
      {selectedTrip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl border-2 border-[#F9A825]">
            <CardHeader>
              <CardTitle className="text-[#004C97]">Trip Details & Driver Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Customer</p>
                  <p className="text-base font-semibold">{selectedTrip.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Patient</p>
                  <p className="text-base font-semibold">{selectedTrip.patient_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Date & Time</p>
                  <p className="text-base">{selectedTrip.date} at {selectedTrip.time}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Mobility Type</p>
                  <p className="text-base">{selectedTrip.mobility_type}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-gray-500">Pickup</p>
                  <p className="text-base">{selectedTrip.pickup_address}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-gray-500">Destination</p>
                  <p className="text-base">{selectedTrip.destination}</p>
                </div>
                {selectedTrip.phone && (
                  <div className="col-span-2">
                    <p className="text-sm font-medium text-gray-500">Phone</p>
                    <p className="text-base">{selectedTrip.phone}</p>
                  </div>
                )}
              </div>

              {!selectedTrip.assigned_driver_id && (
                <>
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Assign Driver
                    </label>
                    <select
                      value={selectedDriver}
                      onChange={(e) => setSelectedDriver(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
                    >
                      <option value="">Select a driver...</option>
                      {drivers
                        .filter(d => d.status === 'Available')
                        .map(driver => (
                          <option key={driver.id} value={driver.id}>
                            {driver.name} - {driver.vehicle_type} ({driver.license_plate})
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      onClick={handleAssignDriver}
                      disabled={!selectedDriver}
                      className="flex-1 bg-[#F9A825] hover:bg-[#E08E1E] text-white"
                    >
                      Assign Driver
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedTrip(null);
                        setSelectedDriver('');
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}

              {selectedTrip.assigned_driver_id && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800 mb-1">Driver Assigned</p>
                  <p className="text-base font-semibold text-green-900">
                    {selectedTrip.assigned_driver_name}
                  </p>
                  <Button
                    onClick={() => setSelectedTrip(null)}
                    variant="outline"
                    className="mt-3 w-full"
                  >
                    Close
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DispatchBoard;
